import {
  getSystemLastUpdatedTimestamp,
  type SystemLastUpdatedLike,
} from "@/lib/system-last-updated";
import type { OnboardingStep } from "@/types/onboarding";
import type { GuidanceStepId, GuidanceStepStatus } from "@/types/guidance";

export const GUIDANCE_STEP_ORDER: GuidanceStepId[] = [
  "add_connector",
  "add_api",
  "configure_auth",
];

type GuidanceStatusMap = Record<GuidanceStepId, GuidanceStepStatus>;

const DEFAULT_STATUS_MAP: GuidanceStatusMap = {
  add_connector: "current",
  add_api: "pending",
  configure_auth: "pending",
};

function createCompletionMap(steps: OnboardingStep[] | null | undefined) {
  const completionMap: Record<GuidanceStepId, boolean> = {
    add_connector: false,
    add_api: false,
    configure_auth: false,
  };

  if (!steps || steps.length === 0) {
    return completionMap;
  }

  for (const step of steps) {
    if (step.step_id in completionMap) {
      completionMap[step.step_id as GuidanceStepId] = Boolean(step.is_completed);
    }
  }

  return completionMap;
}

export function resolveGuidanceStepStatuses(
  steps: OnboardingStep[] | null | undefined,
): GuidanceStatusMap {
  const completionMap = createCompletionMap(steps);
  const firstIncompleteIndex = GUIDANCE_STEP_ORDER.findIndex(
    (stepId) => !completionMap[stepId],
  );

  if (firstIncompleteIndex === -1) {
    return {
      add_connector: "completed",
      add_api: "completed",
      configure_auth: "completed",
    };
  }

  const statuses: GuidanceStatusMap = { ...DEFAULT_STATUS_MAP };
  GUIDANCE_STEP_ORDER.forEach((stepId, index) => {
    if (completionMap[stepId]) {
      statuses[stepId] = "completed";
      return;
    }

    statuses[stepId] = index === firstIncompleteIndex ? "current" : "pending";
  });

  return statuses;
}

type ConnectorSummary = SystemLastUpdatedLike & {
  system_id: string;
  status?: string;
  auth_status?: string;
  is_template?: boolean;
  is_owner?: boolean;
};

function isMoreRecent(left: ConnectorSummary, right: ConnectorSummary): boolean {
  const diff = getSystemLastUpdatedTimestamp(left) - getSystemLastUpdatedTimestamp(right);
  return diff !== 0 ? diff > 0 : left.system_id > right.system_id;
}

/**
 * The connector Getting Started waits on: one the workspace owns that has no
 * authentication yet. A connector is persisted when its spec is processed, so
 * the server counts it as step 1 even when the browser that started it no
 * longer holds the draft (a closed private window, another device).
 * Picks the most recently updated one whatever order `systems` arrives in, so
 * the home page and the sidebar agree.
 */
export function findUnfinishedConnector<T extends ConnectorSummary>(
  systems: T[] | null | undefined,
): T | null {
  let latest: T | null = null;
  for (const system of systems ?? []) {
    // The list marks the workspace's own rows is_owner:true and omits it on
    // others (read_only rows are never owned). An archived connector was
    // retired on purpose, not left unfinished.
    if (
      system.is_owner !== true ||
      system.is_template ||
      system.status === "archived" ||
      system.auth_status !== "not_configured"
    ) {
      continue;
    }
    if (!latest || isMoreRecent(system, latest)) {
      latest = system;
    }
  }
  return latest;
}

export function countCompletedGuidanceSteps(
  steps: OnboardingStep[] | null | undefined,
): number {
  const completionMap = createCompletionMap(steps);
  return GUIDANCE_STEP_ORDER.filter((stepId) => completionMap[stepId]).length;
}
